"use client";

import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { cn } from "../../../../shared/utils/cn.js";
import { formatTokenAmount, safeBigInt } from "../../../../shared/utils/number.js";
import { useMemo } from "react";
export function CartSummary({
  total,
  tokenSymbol,
  classes,
  subtotal,
  tokenDecimals = 18,
  shipping,
  tax,
  discount,
  summaryLines,
  usdEquivalent,
  pricesLoading = false
}) {
  const formattedShipping = useMemo(() => !pricesLoading && shipping?.amount ? formatTokenAmount(safeBigInt(shipping.amount), tokenDecimals) : null, [pricesLoading, shipping?.amount, tokenDecimals]);
  const formattedTax = useMemo(() => !pricesLoading && tax?.amount ? formatTokenAmount(safeBigInt(tax.amount), tokenDecimals) : null, [pricesLoading, tax?.amount, tokenDecimals]);
  const formattedDiscount = useMemo(() => !pricesLoading && discount?.amount ? formatTokenAmount(safeBigInt(discount.amount), tokenDecimals) : null, [pricesLoading, discount?.amount, tokenDecimals]);
  const formattedSummaryLines = useMemo(() => pricesLoading ? undefined : summaryLines?.map(line => ({
    ...line,
    formattedAmount: formatTokenAmount(safeBigInt(line.amount), tokenDecimals),
    isNegative: safeBigInt(line.amount) < BigInt(0)
  })), [pricesLoading, summaryLines, tokenDecimals]);
  const hasAdjustments = !!formattedShipping || !!formattedTax || !!formattedDiscount || !!formattedSummaryLines?.length;
  return _jsxs("div", {
    className: cn("border-t border-gray-200 pt-3 dark:border-neutral-700", classes?.cartSummary),
    children: [hasAdjustments && subtotal && _jsxs("div", {
      className: cn("flex items-center justify-between py-1", classes?.cartSubtotal),
      children: [_jsx("span", {
        className: "text-sm text-gray-500 dark:text-gray-400",
        children: "Subtotal"
      }), _jsxs("span", {
        className: "text-sm text-gray-500 dark:text-gray-400",
        children: [subtotal, " ", tokenSymbol]
      })]
    }), formattedShipping && _jsxs("div", {
      className: cn("flex items-center justify-between py-1", classes?.cartSummaryLine),
      children: [_jsx("span", {
        className: cn("text-sm text-gray-500 dark:text-gray-400", classes?.cartSummaryLineLabel),
        children: shipping?.label || "Shipping"
      }), _jsxs("span", {
        className: cn("text-sm text-gray-500 dark:text-gray-400", classes?.cartSummaryLineAmount),
        children: [formattedShipping, " ", tokenSymbol]
      })]
    }), formattedTax && _jsxs("div", {
      className: cn("flex items-center justify-between py-1", classes?.cartSummaryLine),
      children: [_jsxs("span", {
        className: cn("text-sm text-gray-500 dark:text-gray-400", classes?.cartSummaryLineLabel),
        children: [tax?.label || "Tax", tax?.rate && _jsxs("span", {
          className: "ml-1 text-xs text-gray-400 dark:text-gray-500",
          children: ["(", tax.rate, ")"]
        })]
      }), _jsxs("span", {
        className: cn("text-sm text-gray-500 dark:text-gray-400", classes?.cartSummaryLineAmount),
        children: [formattedTax, " ", tokenSymbol]
      })]
    }), formattedDiscount && _jsxs("div", {
      className: cn("flex items-center justify-between py-1", classes?.cartDiscount),
      children: [_jsxs("span", {
        className: "text-sm text-gray-500 dark:text-gray-400",
        children: [discount?.label || "Discount", discount?.code && _jsx("span", {
          className: "ml-1 rounded bg-green-50 px-1.5 py-0.5 text-xs font-medium text-green-600 dark:bg-green-900/20 dark:text-green-400",
          children: discount.code
        })]
      }), _jsxs("span", {
        className: "text-sm font-medium text-green-600 dark:text-green-400",
        children: ["-", formattedDiscount, " ", tokenSymbol]
      })]
    }), formattedSummaryLines?.map(line_0 => _jsxs("div", {
      className: cn("flex items-center justify-between py-1", classes?.cartSummaryLine),
      children: [_jsxs("span", {
        className: cn("text-sm text-gray-500 dark:text-gray-400", classes?.cartSummaryLineLabel),
        children: [line_0.label, line_0.description && _jsxs("span", {
          className: "ml-1 text-xs text-gray-400 dark:text-gray-500",
          children: ["(", line_0.description, ")"]
        })]
      }), _jsxs("span", {
        className: cn("text-sm", line_0.isNegative ? "font-medium text-green-600 dark:text-green-400" : "text-gray-500 dark:text-gray-400", classes?.cartSummaryLineAmount),
        children: [line_0.formattedAmount, " ", tokenSymbol]
      })]
    }, line_0.label)), _jsxs("div", {
      className: cn("flex flex-col", hasAdjustments && "mt-1 border-t border-gray-100 pt-2 dark:border-neutral-800"),
      children: [_jsxs("div", {
        className: "flex items-center justify-between",
        children: [_jsx("span", {
          className: "text-base font-semibold text-gray-900 dark:text-gray-100",
          children: "Total"
        }), _jsxs("span", {
          className: cn("text-base font-semibold text-gray-900 dark:text-gray-100", classes?.cartTotal),
          children: [total, " ", tokenSymbol]
        })]
      }), !pricesLoading && usdEquivalent && _jsx("div", {
        className: "flex justify-end",
        children: _jsxs("span", {
          className: "text-xs text-gray-400 dark:text-gray-500",
          children: ["~", usdEquivalent, " USD"]
        })
      })]
    })]
  });
}